import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, Form, Formik, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  FormButtonPanelUI,
  ColorCircleUi,
  ChromeColorPickerUI,
} from '../Interface';
import Yup from '../../utils/yup';
import { useEffect, useState } from 'react';
import simpleTaskDeadLineNotificationService from '../../service/simpleTaskDeadLineNotification.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { Box, Popover } from '@mui/material';

function EditToolbar(props) {
  const { push, defDeadLineNotification, values, validateField } = props;

  const handleClick = async () => {
    const newData = {
      ...defDeadLineNotification,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `deadLineNotification.${values.deadLineNotification.length ?? 0}.daysBefore`,
      `deadLineNotification.${values.deadLineNotification.length ?? 0}.color`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const SimpleTaskDeadLineNotificationPage = ({ dialog }) => {
  const apiRef = useGridApiRef();
  const [formValues, setFormValues] = useState({
    deadLineNotification: [],
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const defDeadLineNotification = {
    daysBefore: null,
    color: '#FFFF',
    isNew: true,
  };

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    simpleTaskDeadLineNotificationService.getData().then((data) => {
      setFormValues({
        deadLineNotification: data,
      });
    });
  };

  const getValueIndex = (values, id) => {
    return values.deadLineNotification.findIndex((rc) => rc.id === id);
  };

  const handleSubmit = (values) => {
    simpleTaskDeadLineNotificationService.rowData(values).then(() => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      if (dialog) {
        dialog.handleClose();
      }
    });
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;

  const columns = (setFieldValue, validateField, errors, remove, values) => {
    return dataGridPremiumColumns([
      {
        field: 'daysBefore',
        headerName: 'Ennyi nappal előtte',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`deadLineNotification.${valueIndex}.daysBefore`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.deadLineNotification?.[valueIndex]?.daysBefore} />}
              onChange={(e) => {
                setFieldValue(`deadLineNotification.${valueIndex}.daysBefore`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`deadLineNotification.${valueIndex}.daysBefore`)}
            />
          );
        },
      },
      {
        field: 'color',
        headerName: 'Szín',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          return <ColorCircleUi color={params.value ?? '#FFFFFF'} size={20} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <div className="ml-1 w-[100%] flex justify-center">
              <div
                className="cursor-pointer rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-2.5"
                style={{ background: values.deadLineNotification[valueIndex].color }}
                onClick={handlePopoverOpen}
              />
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
              >
                <ChromeColorPickerUI
                  color={values.deadLineNotification[valueIndex].color}
                  onChange={(color) => {
                    setFieldValue(`deadLineNotification.${valueIndex}.color`, color.hex);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: color.hex,
                    });
                  }}
                />
              </Popover>
            </div>
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
            />,
          ];
        },
      },
    ]);
  };

  const validationSchema = Yup.object().shape({
    deadLineNotification: Yup.array().of(
      Yup.object().shape({
        daysBefore: Yup.number().required(),
        color: Yup.string().required(),
      })
    ),
  });

  return (
    <div className="overflow-x-auto mt-2">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <Formik
            initialValues={formValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, setFieldValue, validateField }) => (
              <Form>
                <FieldArray name="deadLineNotification">
                  {({ remove, push }) => (
                    <DatagridPremiumUi
                      apiRef={apiRef}
                      datagridIdentifier={DataGridIdentifiers.DeadLineNotification}
                      height={400}
                      columns={columns(setFieldValue, validateField, errors, remove, values)}
                      rows={values?.deadLineNotification ?? []}
                      slots={{
                        toolbar: EditToolbar,
                      }}
                      headerFilters={false}
                      slotProps={{
                        toolbar: {
                          push,
                          values,
                          validateField,
                          defDeadLineNotification,
                        },
                      }}
                      onCellDoubleClick={(_params, event) => {
                        event.defaultMuiPrevented = true;
                      }}
                      editMode="row"
                      getRowHeight={() => 'auto'}
                      onCellClick={(params, event) => {
                        const rowMode = apiRef.current.getRowMode(params.row.id);
                        if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                      }}
                    />
                  )}
                </FieldArray>
                <Box mt={2}>
                  <FormButtonPanelUI
                    onCancelClick={() => {
                      if (dialog) {
                        dialog.handleClose();
                      }
                    }}
                  />
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default SimpleTaskDeadLineNotificationPage;
